import { userSync } from './helpers/userSync'
import { timesheetSync } from './helpers/timesheetSync'
import { LastSyncModel } from '../models/LastSyncModel'
import { useStoreProfiles } from '../store/useStoreProfiles'
import { getStoreEditedData } from './helpers/storesSync'
import { useAPIURLs } from '../composables/useAPIURLs'
import { storeToRefs } from 'pinia'
import { useTimers } from '../store/timers'
import { useMessages } from '../store/useMessages'
import { useAuthenticateSAML } from '../store/useAuthenticateSAML'
import { Notify } from 'quasar'
import { useTaskProfiles } from '../store/useTaskProfiles'
import { useRecapStore } from '../store/useRecapStore'
import { SyncData, SyncResponse } from '../types/sync'
import { useCurrentCycle } from '../store/useCurrentCycle'
import { saveExpensesFromTimecards } from './helpers/expensesSync'
import { RecapResponsesModel } from '../models/RecapReponsesModel'
import { ref, unref } from 'vue'
import { RecapDefinitionsModel } from '../models/RecapDefinitionsModel'
import { StoreRecapDefinitionsModel } from '../models/StoreRecapDefinitionsModel'
import { getCompletedTasksData } from './helpers/tasksSync'
import { Balances, useInventory } from '../store/useInventory'
import { useCodes } from '../store/useCodes'
import { Capacitor } from '@capacitor/core'
import { Http } from '@capacitor-community/http'
import { flagSuccesfulTransactions, transactions } from './helpers/transactions'
import { usePayrollWindow } from '../store/usePayrollWindow'
import { attachmentQueue } from './helpers/fetchAttachments'
import { partImageQueue } from './helpers/fetchPartImages'
import {
  createWarningNotification,
  logoutAndRedirect,
  sync200BadFormatHandler,
  sync401Error,
  syncBadResponseHandler,
  syncErrorHandler,
} from './syncErrorHandler'
import { useStoreRecaps } from '../store/useStoreRecaps'
import { StoreRecapResponsesModel } from '../models/StoreRecapResponsesModel'
import { useCheckVersion } from '../composables/useCheckVersion'
import { TimersModel } from '../models/TimersModel'
import { addBreadcrumb, captureException } from '@sentry/vue'
import { Device } from '@capacitor/device'
import { useUserStore } from '../store/useUserStore'
import { recapResponsesSync } from './helpers/recapResponsesSync'
import { recapImagesSync } from './helpers/recapImagesSync'
import { AuthenticationModel } from '../models/Authentication'
import { useGlobalTimesheetInstance } from '../store/useGlobalTimesheetInstance'

export const isSyncing = ref(false)
export const currentSyncStep = ref<string | null>(null)
export const syncProgress = ref(100)
const initPayloadIds = { task_ids: [] }
export const payloadIds = ref<{ task_ids: number[] }>({ ...initPayloadIds })

export const SyncDatabase = () => {
  const { lastSyncDate } = LastSyncModel()

  /**
   * Here we load up data to post.
   * Last sync: date last synced.
   * User - If the rep has no data, a flag will be present on the sync payload.
   *  If the rep has data, the last sync date is used to check if there have
   *  been any updates. (User updates are slower than 30 days)
   */
  const getSyncData = async (data: {
    syncTimesheetData?: { agree_legal: boolean; claim_travel_override: boolean }
  }): Promise<SyncData> => {
    // Reset the sync progress
    setSyncProgress('Beginning sync', 0)

    const last_sync = await lastSyncDate()

    const { getAllCompleteRecapResponses } = RecapResponsesModel()
    const { getAllStoreRecapResponses } = StoreRecapResponsesModel()

    const { hasUserData } = userSync()
    const { storesHash } = useStoreProfiles()
    const { tasksHash } = useTaskProfiles()
    const { partsHash } = storeToRefs(useInventory())
    const { getCodesHashes } = useCodes()
    //unref store Active
    const { storeActive, getAllParentTimers } = storeToRefs(useTimers())
    const storeIsActive = unref(storeActive.value)
    const { getRecapHashes, getRecapTaskMappingHashes } = useRecapStore()
    const recap_responses = await getAllCompleteRecapResponses()
    const store_recap_responses = await getAllStoreRecapResponses()
    const { getStoreRecapsHash } = useStoreRecaps()
    //Messages sent/replied by rep
    const { getMessagesAfterDate, getReadReceiptsAfterDate, getDeletedMessages } = useMessages()
    const messagesToSync = getMessagesAfterDate(last_sync)
    const readReceiptsToSync = getReadReceiptsAfterDate(last_sync)
    const deletedMessages = getDeletedMessages()
    const { requestCycleData } = storeToRefs(useCurrentCycle())
    const { requestPayrollData } = usePayrollWindow()
    const updated_stores = getStoreEditedData()
    const updated_tasks = getCompletedTasksData()

    setSyncProgress('Calculate hashes', 5)
    //Calculate hashes
    const storesHashValue = storeIsActive ? '' : await storesHash()
    const tasksHashValue = storeIsActive ? '' : await tasksHash()
    const codesHashValue = await getCodesHashes()

    //meta
    setSyncProgress('Gathering data', 10)

    //timesheet
    //Only gather if end of day
    let timesheet
    if (data.syncTimesheetData) {
      const { getTimeSheet } = useTimers()
      const {
        model,
        operatingSystem,
        osVersion,
        platform,
        manufacturer,
        isVirtual,
        name,
        webViewVersion,
      } = await Device.getInfo()
      timesheet = {
        ...data.syncTimesheetData,
        ...getTimeSheet()!,
        meta: {
          staff_app_version: process.env.ANDROID_VERSION_NAME ?? null,
          ...{
            model,
            operatingSystem,
            osVersion,
            platform,
            manufacturer,
            isVirtual,
            name,
            webViewVersion,
          },
        },
      }
      if (
        !timesheet.date ||
        (!timesheet.activity_data.length && getAllParentTimers.value.length) ||
        !timesheet.activity_data.length
      ) {
        //capture any weirdness, cancel sync
        addBreadcrumb({
          category: 'timecard generation',
          message: 'timecard generation failure',
          level: 'error',
          data: { timers: getAllParentTimers.value, timesheetData: timesheet },
        })
        console.error({ timers: getAllParentTimers.value })
        console.error({ timesheetData: timesheet })
        captureException('Timecard generation failure')
      }

      //stop sync if active timer
      if (
        timesheet.activity_data.length < 1 ||
        timesheet.activity_data.some((timer) => !timer['end_time'])
      ) {
        createWarningNotification({ message: 'Please end your activity before submitting' })
        throw new Error('Cannot submit timecard with active timers')
      }
    } else {
      //Let server handle absence of timesheet
      timesheet = null
    }

    //updated timecards/timesheet
    const { hasReadyToSyncTimesheets, getTimesheetsToSync } = timesheetSync()
    let updatedTimesheets
    if (hasReadyToSyncTimesheets.value) {
      updatedTimesheets = getTimesheetsToSync()
      for (const ts of updatedTimesheets) {
        //stop sync if active timer
        if (ts.activity_data.some((timer) => !timer['end_time'])) {
          createWarningNotification({
            message: `Please end your activity before submitting (date: ${new Date(
              ts.date
            ).toLocaleDateString()})`,
          })
          throw new Error('Cannot submit timecard with active timers')
        }
        const { deviceDate } = ts
        const {
          model,
          operatingSystem,
          osVersion,
          platform,
          manufacturer,
          isVirtual,
          name,
          webViewVersion,
        } = await Device.getInfo()
        ts.meta = {
          staff_app_version: process.env.ANDROID_VERSION_NAME ?? null,
          ...{
            deviceDateForTimesheet: deviceDate ?? null,
            model,
            operatingSystem,
            osVersion,
            platform,
            manufacturer,
            isVirtual,
            name,
            webViewVersion,
          },
        }

        if (ts.activity_data.length < 1) {
          //capture any resubmission weirdness, cancel sync
          addBreadcrumb({
            category: 'Updated timecard generation',
            message: 'Updated timecard generation failure',
            level: 'error',
            data: { timesheetData: ts },
          })
          console.error({ timesheetData: ts })
          captureException('Updated timecard generation failure')
        }
      }
    } else {
      updatedTimesheets = null
    }
    //get transactions to sync
    const transactionsToSync = await transactions(last_sync)

    // NPUB-683
    // We're having some weird issue where, for some reason, we have duplicate recap responses
    // Ideally I'd fix the root cause, but it's proving to be a pretty elusive bug
    // The good news is that we've finally traced it back to the app,
    // so this should at least fix the duplicates
    const { filterResponses, resetTaskRecapData } = recapResponsesSync()
    const { filtered: filteredResponses, duplicates: duplicateResponses } = filterResponses(
      recap_responses,
      storeIsActive,
      'task'
    )
    const { filtered: filteredStoreResponses } = filterResponses(
      store_recap_responses,
      storeIsActive,
      'venue'
    )

    const syncData: SyncData = {
      meta: {
        last_synced: last_sync.toISOString(),
        has_user_data: hasUserData.value,
        skip_stores_tasks_recaps: storeIsActive,
        get_current_cycle: requestCycleData.value,
        get_payroll_window: requestPayrollData(),
        language_preference: useUserStore().language,
      }, //fix
      current_timesheet: timesheet,
      updated_timesheets: updatedTimesheets,
      //hashes
      stores_hash: storesHashValue,
      tasks_hash: tasksHashValue,
      all_parts_hash: partsHash.value,
      codes_hashes: codesHashValue,
      recap_hashes: await getRecapHashes(),
      recap_task_mapping_hashes: await getRecapTaskMappingHashes(),
      store_recap_hashes: await getStoreRecapsHash(),
      //updated data
      updated_stores,
      updated_tasks: storeIsActive ? null : updated_tasks,
      messages: {
        outbox: messagesToSync,
        read_receipts: readReceiptsToSync,
        deleted_messages: deletedMessages,
      },
      inventory_transactions: transactionsToSync,
      recap_responses: filteredResponses,
      store_recap_responses: filteredStoreResponses,
    }

    // Add recap images to the sync data
    setSyncProgress('Task images', 20)
    const { setTaskImages, setStoreImages } = recapImagesSync(setSyncProgress)
    const duplicateTaskData = await setTaskImages(syncData)
    setSyncProgress('Store images', 24)
    await setStoreImages(syncData)
    // Before we sync, make sure to reset any invalid/duplicate tasks/recap responses
    setSyncProgress('Cleaning up data', 28)
    await resetTaskRecapData(syncData, duplicateTaskData, duplicateResponses)

    return syncData
  }

  const { API } = useAPIURLs()
  const postSync = async (syncData: SyncData) => {
    console.log('posting', API.value.sync, syncData)

    // This should already be true, but make sure it is just in case
    isSyncing.value = true

    //safety check for login
    const { getJWT } = storeToRefs(useAuthenticateSAML())
    if (!getJWT.value || !getJWT.value.token) {
      const { loadJWT } = AuthenticationModel()
      // Send to our error tracing service. Tokens are filtered.
      console.error(await loadJWT())
      console.error(getJWT.value)
      console.error({ hasToken: !!getJWT.value?.token })
      console.error('No JWT')
      captureException('No JWT')
      await logoutAndRedirect()
      return void 0
    }

    setSyncProgress('Syncing', 30)

    setPayloadIds(syncData)

    return await post({
      url: API.value.sync,
      headers: {
        Authorization: `Bearer ${getJWT.value?.token}`,
        'Content-Type': 'application/json',
        'Accept-Encoding': 'gzip',
      },
      data: syncData,
    })
  }

  const sync = async (data?: {
    timesheetMeta?: { agree_legal: boolean; claim_travel_override: boolean }
  }) => {
    if (isSyncing.value) {
      return
    }
    const { getJWT } = storeToRefs(useAuthenticateSAML())
    if (!getJWT.value || !getJWT.value.token) {
      const { loadJWT } = AuthenticationModel()
      // Send to our error tracing service. Tokens are filtered.
      console.error(await loadJWT())
      console.error(getJWT.value)
      console.error({ hasToken: !!getJWT.value?.token })
      console.error('No JWT')
      captureException('No JWT')
      await logoutAndRedirect()
      return
    }
    console.log('start sync')
    isSyncing.value = true
    // only grab timesheet after rep has gone through end day
    let syncTimesheetData
    if (data && data.timesheetMeta) {
      syncTimesheetData = data.timesheetMeta
    }

    const syncedEvent = new CustomEvent('syncStarted')
    window.dispatchEvent(syncedEvent)

    //get and post sync data
    return await getSyncData({ syncTimesheetData })
      .then(async (res) => {
        return await postSync(res)
      })
      .finally(async () => {
        await useCheckVersion().checkAvailable()
        isSyncing.value = false
        resetPayloadIds()
      })
  }
  return { sync, postSync, getSyncData }
}

//interface for sync post
//cordova available only for native builds, so we may use
//the http plugin for spa builds
const post = async (data: {
  url: string
  headers: {
    Authorization: string
    'Content-Type': string
    'Accept-Encoding': string
  }
  // data: FormData
  data: SyncData
}) => {
  if (Capacitor.isNativePlatform()) {
    //@ts-expect-error cordova is not defined.
    cordova.plugin.http.setDataSerializer('json')
    //@ts-expect-error cordova is not defined
    cordova.plugin.http.setConnectTimeout(90.0)
    //@ts-expect-error cordova is not defined
    cordova.plugin.http.setReadTimeout(600.0)
    return await new Promise((resolve, reject) => {
      //@ts-expect-error cordova is not defined
      cordova.plugin.http.post(
        data.url,
        data.data,
        data.headers,
        (r: any) => {
          // call the async success() and handle its result
          success(r).then(resolve).catch(reject)
        },
        (err: any) => {
          if (err.status) {
            syncBadResponseHandler(err)
          } else {
            syncErrorHandler(err)
          }
          reject(err)
        }
      )
    })
  } else {
    return await Http.post({
      url: data.url,
      headers: data.headers,
      data: data.data,
    })
      .catch(syncErrorHandler)
      .then((res) => success(res))
  }
}

//Logic to deal with different potential errors that may arise during sync before passing response to syncCallback() to save data
async function success(res: any) {
  console.log('success', res)
  const responseData: any = {}

  if (res && res.data) {
    sync200BadFormatHandler(res)
    responseData['data'] = res.data
    responseData['response'] = res.status
  }
  //alert problems here
  if (res.status && res.status != 200) {
    if (res.status == 401) {
      return await sync401Error(res)
    }
    syncBadResponseHandler(res)
  } else if (
    responseData.error ||
    (responseData.data && responseData.data.error && responseData.data.ref_id)
  ) {
    syncBadResponseHandler(responseData)
  } else {
    //only sync on successful callbacks
    console.log('res status', res.status)
    await syncCallback(responseData)
  }

  return res
}

//logic to handle updating data from successful syncs
const syncCallback = async (syncResponse: SyncResponse) => {
  const { storeActive } = storeToRefs(useTimers())

  //Handle syncing errors
  if (
    syncResponse.error ||
    (syncResponse.data && syncResponse.data.error && syncResponse.data.ref_id)
  ) {
    console.log('Error syncing')
    Notify.create({
      type: 'negative',
      timeout: 0,
      actions: [
        {
          icon: 'close',
          color: 'white',
          // for individual action (button):
          'aria-label': 'Dismiss',
        },
      ],
      position: 'bottom-left',
      message: `An error ocurred during data sync. ${
        syncResponse.data && syncResponse.data.ref_id
          ? 'Ref ID: ' + syncResponse.data.ref_id
          : undefined
      }`,
    })
  } else if (syncResponse.response && syncResponse.response === 200) {
    //Successful
    setSyncProgress('Syncing', 68)
    console.log({ syncResponse })

    //User data
    const setUserData = async () => {
      const { storeUserData } = userSync()
      if (syncResponse.data.meta.user_data) {
        const userData = syncResponse.data.meta.user_data
        await storeUserData(userData)
      }
    }
    const setStores = async () => {
      if (!storeActive.value) {
        //New stores
        const newStoresJson = syncResponse.data.new_data.stores
        if (newStoresJson) {
          await useStoreProfiles().setStores(newStoresJson as any)
        } else {
          //if we have sent up store data, reset 'edited' flags
          if (getStoreEditedData()?.length) {
            const { resetStoreEditedFlags } = useStoreProfiles()
            resetStoreEditedFlags()
          }
        }
      }
    }
    const setTasks = async () => {
      if (!storeActive.value) {
        if (syncResponse.data.remove && syncResponse.data.remove.tasks) {
          const removeTasks = syncResponse.data.remove.tasks
          await useTaskProfiles().removeTasks(removeTasks)
        }
        if (syncResponse.data.remove && syncResponse.data.new_data.tasks) {
          const newTasksJson = syncResponse.data.new_data.tasks
          await useTaskProfiles().setTasks(newTasksJson)
        }
      }
    }
    const setRecaps = async () => {
      const recapsJSON = syncResponse.data.new_data.recaps
      if (recapsJSON && (recapsJSON.recaps || recapsJSON.task_mappings)) {
        const { writeRecapDefinitions } = RecapDefinitionsModel()
        await writeRecapDefinitions(recapsJSON).catch((error) =>
          console.error(`Error writing recap definitions to database from sync: ${error}`)
        )
      }
    }
    const deleteOldRecapResponses = async () => {
      const postedRecapResponses = syncResponse.data.new_data.recap_responses
      if (postedRecapResponses && postedRecapResponses.length) {
        const { removeByTaskId } = RecapResponsesModel()
        const recapStore = useRecapStore()
        for (const response of postedRecapResponses) {
          await removeByTaskId(response.task_id)
          const matchingResponseIndex = recapStore.recap_responses?.findIndex(
            (res) => +res.task_id! === +response.task_id
          )
          if (matchingResponseIndex !== null && matchingResponseIndex > -1) {
            recapStore.recap_responses.splice(matchingResponseIndex, 1)
          }
          //remove definition from memory
          recapStore.removeRecapFromStore(response.events_recaps_id, response.task_id)
        }
      }
    }
    const setStoreRecaps = async () => {
      const recapsJSON = syncResponse.data.new_data.store_recaps
      if (recapsJSON && recapsJSON.recaps && recapsJSON.venue_mappings) {
        const { writeStoreRecapDefinitions } = StoreRecapDefinitionsModel()
        await writeStoreRecapDefinitions(recapsJSON.recaps, recapsJSON.venue_mappings).catch(
          (error) =>
            console.error(`Error writing store recap definitions to database from sync: ${error}`)
        )
      }
    }
    const deleteOldStoreRecapResponses = async () => {
      const postedRecapResponses = syncResponse.data.new_data.store_recap_responses
      if (postedRecapResponses && postedRecapResponses.length) {
        const { removeStoreResponsesForSyncedResponseWithStoreId } = StoreRecapResponsesModel()
        const recapStore = useStoreRecaps()
        for (const response of postedRecapResponses) {
          await removeStoreResponsesForSyncedResponseWithStoreId(
            response.store_id,
            response.events_recaps_id
          )
          const matchingResponseIndex = recapStore.recap_responses?.findIndex(
            (res) =>
              +res.store_id! === +response.venue_id &&
              +res.events_recaps_id === +response.events_recaps_id
          )
          if (matchingResponseIndex !== null && matchingResponseIndex > -1) {
            recapStore.recap_responses.splice(matchingResponseIndex, 1)
          }
          //remove definition from memory
          recapStore.removeRecapFromStore(response.events_recaps_id, response.venue_id)
        }
      }
    }
    const setMessages = async () => {
      //Add new messages.
      const { addMessages, removeMessagesDeleteBeforeDate } = useMessages()
      const messages = syncResponse.data.new_data.messages
      const date = new Date(syncResponse.data.meta.sync_date)
      if (messages) {
        await addMessages(syncResponse.data.new_data.messages)
        removeMessagesDeleteBeforeDate(date)
        //add any attachments to a separate queue for download
        const allAttachments = messages
          .map((message: any) => {
            return message.attachments
          })
          .flat()
        if (Capacitor.isNativePlatform()) {
          const { addToDownloadAttachmentQueue } = attachmentQueue()
          addToDownloadAttachmentQueue(allAttachments)
        }
      }
    }

    const setTimesheets = async () => {
      const {
        saveNewTimesheets,
        removeBeyondPayPeriod,
        timesheetForDay,
        setTimesheetTimersAndLock,
      } = timesheetSync()
      //sets timesheets and expenses
      if (syncResponse.data.activities && syncResponse.data.activities.timesheets) {
        const newTimesheets = syncResponse.data.activities.timesheets
        await saveNewTimesheets(newTimesheets)
        saveExpensesFromTimecards(newTimesheets)
        //flag the timecards that we have generated a timecard on the device so they don't
        //get removed,
        for (const ts of newTimesheets) {
          await TimersModel().flagTimecardGenerated(ts.date.replaceAll(' 00:00:00', ''))
        }
      }
      const timesheetEditDate = useGlobalTimesheetInstance().timesheet?.date ?? null
      //now clear timesheets that aren't in the payroll window
      await removeBeyondPayPeriod(timesheetEditDate)

      //now that those are gone, we check to see if the rep's just logged in,
      //and if there's a timesheet for the current day
      //we then set the day's timers, keep them locked
      //if timesheet day is today
      //set current timers
      //lock em
      const today = new Date()
      if (syncResponse.data.activities && syncResponse.data.activities.timesheets) {
        const newTimesheets = syncResponse.data.activities.timesheets

        //only check ts for day from a sync, otherwise it would reset from every boot
        if (timesheetForDay(today, newTimesheets)) {
          console.log('setting ts from sync')
          //set our timers and lock them
          await setTimesheetTimersAndLock(today)
        }
      }
    }

    const setLastSyncDate = async () => {
      //update last sync date
      await LastSyncModel()
        .setLastSyncDate(syncResponse.data.meta.sync_date)
        .catch((error) => {
          console.log(`error setting last sync: ${error}`)
        })
    }

    const setCurrentCycle = async () => {
      if (syncResponse.data.new_data.cycle_data) {
        const { setCycle } = useCurrentCycle()
        const { begin_date, end_date, ...rest } = syncResponse.data.new_data.cycle_data
        const newData = {
          begin_date: new Date(begin_date),
          end_date: new Date(end_date),
          ...rest,
        }
        await setCycle(newData)
      }
    }

    const setCurrentPayroll = async () => {
      if (syncResponse.data.new_data.payroll_window) {
        const { setPayroll } = usePayrollWindow()
        const {
          start,
          end,
          approval_cutoff_date,
          last_start,
          last_end,
          last_approval_cutoff_date,
        } = syncResponse.data.new_data.payroll_window

        const newData = {
          start: new Date(start),
          end: new Date(end),
          approval_cutoff_date: new Date(approval_cutoff_date),
          last_start: new Date(last_start),
          last_end: new Date(last_end),
          last_approval_cutoff_date: new Date(last_approval_cutoff_date),
        }
        await setPayroll(newData)
      }
    }

    const setParts = async () => {
      if (syncResponse.data.new_data.all_parts) {
        const allPartsNew = syncResponse.data.new_data.all_parts
        if (allPartsNew) {
          useInventory().setAllParts(allPartsNew)
          if (Capacitor.isNativePlatform()) {
            const allImages = allPartsNew
              .map((part: any) => {
                return part.image
              })
              .flat()
            const { addToDownloadAttachmentQueue } = partImageQueue()
            addToDownloadAttachmentQueue(allImages)
          }
        }
      }
    }

    const removeTransactions = async () => {
      if (syncResponse.data.remove && syncResponse.data.remove.transactions) {
        const removeUUIDs = syncResponse.data.remove.transactions
        await flagSuccesfulTransactions(removeUUIDs)
      }
    }

    const updateBOH = async () => {
      if (syncResponse.data.new_data.updated_boh) {
        const allBOHNew = syncResponse.data.new_data.updated_boh
        //ensure the types are numbers
        const newBOH: Balances = {}
        for (const part_id in allBOHNew) {
          newBOH[part_id] = Number(allBOHNew[part_id])
          //remove zero vals
          if (!newBOH[part_id]) {
            delete newBOH[part_id]
          }
        }
        useInventory().setBalances(newBOH)
      }
    }

    //sets codes
    const setCodes = async () => {
      if (syncResponse.data.new_data.codes) {
        const newCodes = syncResponse.data.new_data.codes
        const { setCodes } = useCodes()
        await setCodes(newCodes, false)
      }
    }

    const syncQueue = [
      { name: 'Setting rep data', fn: setUserData },
      { name: 'Saving stores', fn: setStores },
      { name: 'Saving sync params', fn: setLastSyncDate },
      { name: 'Saving messages', fn: setMessages },
      { name: 'Saving current cycle', fn: setCurrentCycle },
      { name: 'Saving current payroll', fn: setCurrentPayroll },
      { name: 'Saving timesheets', fn: setTimesheets },
      { name: 'Saving parts', fn: setParts },
      { name: 'Updating transactions', fn: removeTransactions },
      { name: 'Updating balances', fn: updateBOH },
      { name: 'Saving tasks', fn: setTasks },
      { name: 'Saving recaps', fn: setRecaps },
      { name: 'Clearing responses', fn: deleteOldRecapResponses },
      { name: 'Saving store recaps', fn: setStoreRecaps },
      { name: 'Clearing store responses', fn: deleteOldStoreRecapResponses },
      { name: 'Reticulating splines', fn: setCodes },
    ]
    while (syncQueue.length) {
      const queueItem = syncQueue.shift()
      if (queueItem) {
        currentSyncStep.value = queueItem.name
        await queueItem.fn()
        setSyncProgress('Syncing', syncProgress.value + 2)
      }
    }
  }
  isSyncing.value = false
  setSyncProgress(null, 0)
  resetPayloadIds()
}

const setSyncProgress = (message: string | null, percentage: number) => {
  currentSyncStep.value = message
  syncProgress.value = percentage
}
const setPayloadIds = (syncData: SyncData) => {
  payloadIds.value.task_ids = syncData.updated_tasks
    ? syncData.updated_tasks?.map((task) => task.id)
    : []
}
const resetPayloadIds = () => {
  payloadIds.value = { ...initPayloadIds }
}
